import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  RequiredNote,
  SelectField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../Components/Form';
import { useSelector } from 'react-redux';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import './UserFormModal.css';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

const SCHEMA = {
  employee: { label: 'Employee', rules: [required()] },
  roles: {
    label: 'Roles',
    rules: [required('Select at least one role')],
  },
};
const FIELD_ORDER = ['employee', 'roles'];

// Tambah user dari data IDAMAN (Master.UserIdaman). User yang dibuat di sini tidak punya
// password dan hanya bisa login lewat tombol idAMan (lihat AccountController.LoginOidc).
const IdamanUserFormModal = ({ isOpen, toggle, onSuccess }) => {
  const token = useSelector(state => state.Login?.user?.token);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [roles, setRoles] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    if (!isOpen) return;

    setSelectedEmployee(null);
    setRoles([]);
    setErrors({});

    const fetchRoles = async () => {
      try {
        const response = await fetch(
          `${api.API_URL}/role?PageNumber=1&PageSize=200`,
          { headers: authHeaders }
        );
        const result = await response.json();
        setRoleOptions(
          (result.data?.items || []).map(item => ({
            value: item.name,
            label: item.name,
          }))
        );
      } catch (error) {
        setRoleOptions([]);
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const searchEmployees = async query => {
    // Input berisi "@" dicari berdasarkan email, selain itu berdasarkan nama.
    const param = query.includes('@') ? 'Email' : 'DisplayName';
    const response = await fetch(
      `${api.API_URL}/usermanagement/get-user-idaman?PageNumber=1&PageSize=20&${param}=${encodeURIComponent(query)}`,
      { headers: authHeaders }
    );
    if (!response.ok) return [];
    const result = await response.json();
    return (result.data?.items || []).map(item => ({
      value: item.id,
      label: item.displayName || item.email,
      employee: item,
    }));
  };

  const loadOptions = inputValue =>
    new Promise(resolve => {
      clearTimeout(debounceRef.current);
      const query = inputValue.trim();
      if (query.length < MIN_SEARCH_LENGTH) {
        resolve([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          resolve(await searchEmployees(query));
        } catch (error) {
          resolve([]);
        }
      }, SEARCH_DEBOUNCE_MS);
    });

  const formatOptionLabel = ({ employee }) => (
    <div>
      <div className='fw-medium'>{employee.displayName || '-'}</div>
      <small className='text-muted'>
        {employee.email || '-'}
        {employee.employeeNumber ? ` · ${employee.employeeNumber}` : ''}
        {employee.companyCode ? ` · ${employee.companyCode}` : ''}
      </small>
    </div>
  );

  const validate = () => {
    const newErrors = validateForm(
      { employee: selectedEmployee, roles },
      SCHEMA
    );
    setErrors(newErrors);
    if (!hasErrors(newErrors)) return true;
    focusFirstError(newErrors, FIELD_ORDER);
    return false;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const response = await fetch(
        `${api.API_URL}/usermanagement/create-user-from-idaman`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            userIdamanId: selectedEmployee.value,
            roles,
          }),
        }
      );
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || `HTTP error ${response.status}`);
      }

      // BE tetap mengembalikan 200 bila user tercipta tetapi assign role gagal.
      const partial = /role assignment failed/i.test(result.message || '');
      if (partial) {
        notify.warning(result.message);
      } else {
        notify.success(
          `${selectedEmployee.employee.displayName} can now sign in with idAMan.`
        );
      }

      onSuccess?.();
      toggle();
    } catch (error) {
      notify.apiError(error, 'add the user');
    } finally {
      setSubmitting(false);
    }
  };

  const employee = selectedEmployee?.employee;
  const details = employee
    ? [
        ['Email', employee.email],
        ['Username', employee.username],
        ['Employee no.', employee.employeeNumber],
        ['Job title', employee.jobTitle],
        ['Department', employee.department],
        [
          'Company',
          [employee.companyCode, employee.companyName]
            .filter(Boolean)
            .join(' - '),
        ],
      ]
    : [];

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size='lg'
      className='ufm-modal'
      backdrop='static'
    >
      <div className='ufm-header'>
        <div className='ufm-header-left'>
          <div className='ufm-header-icon'>
            <i className='ri-user-add-line' />
          </div>
          <div>
            <h3 className='ufm-title'>Add User from idAMan</h3>
            <p className='ufm-subtitle'>
              Give an idAMan employee access to Hydro
            </p>
          </div>
        </div>
        <button
          className='ufm-close-btn'
          onClick={toggle}
          type='button'
          aria-label='Close'
        >
          <i className='ri-close-line' />
        </button>
      </div>

      <ModalBody className='p-3 p-sm-4'>
        <RequiredNote />
        <FormGrid columns={2}>
          <SelectField
            span='full'
            label='Employee'
            name='employee'
            required
            cacheOptions
            loadOptions={loadOptions}
            value={selectedEmployee}
            error={errors.employee}
            hint={`Type at least ${MIN_SEARCH_LENGTH} characters of a name or email`}
            onChange={option => {
              setSelectedEmployee(option);
              setErrors(prev => ({ ...prev, employee: '' }));
            }}
            formatOptionLabel={formatOptionLabel}
            placeholder='Search by name or email'
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim().length < MIN_SEARCH_LENGTH
                ? `Type at least ${MIN_SEARCH_LENGTH} characters`
                : 'No employee found'
            }
          />

          {employee &&
            details.map(([label, value]) => (
              <TextField
                key={label}
                label={label}
                name={`employee-${label}`}
                mode='readonly'
                value={value || '-'}
              />
            ))}

          <SelectField
            span='full'
            multi
            label='Roles'
            name='roles'
            required
            options={roleOptions}
            value={roles}
            error={errors.roles}
            hint='Roles decide which menus and actions this user can access'
            onChange={selected => {
              setRoles(selected || []);
              setErrors(prev => ({ ...prev, roles: '' }));
            }}
            placeholder='Select one or more roles'
            noOptionsMessage={() => 'No roles available'}
          />

          <div className='ufm-readonly-notice' style={{ gridColumn: '1 / -1' }}>
            <i className='ri-information-line' />
            <div>
              <h6>No password needed</h6>
              <p>
                This user will sign in with the idAMan button on the login page.
                Profile data is synced from idAMan on every sign-in.
              </p>
            </div>
          </div>
        </FormGrid>
      </ModalBody>

      <div className='ufm-footer'>
        <ActionButton variant='secondary' icon='ri-close-line' onClick={toggle}>
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-check-line'
          onClick={handleSubmit}
          loading={submitting}
        >
          {submitting ? 'Processing...' : 'Add User'}
        </ActionButton>
      </div>
    </Modal>
  );
};

export default IdamanUserFormModal;
